import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import ServicesGrid from '../components/ServicesGrid';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import { Users, Zap, GraduationCap, BriefcaseBusiness, ArrowRight, Check } from 'lucide-react';

const AREAS = [
  {
    heading: 'Trauma & Recovery',
    items: ['Trauma Therapy', 'EMDR Therapy', 'Accelerated Resolution Therapy (ART)', 'PTSD & Complex PTSD', 'Childhood Trauma', 'Trauma Recovery'],
  },
  {
    heading: 'Anxiety & Stress',
    items: ['Generalized Anxiety Disorder (GAD)', 'Panic Attacks', 'Social Anxiety', 'Health Anxiety', 'Stress Management'],
  },
  {
    heading: 'Depression',
    items: ['Major Depression', 'Seasonal Depression', 'Persistent Depressive Disorder', 'Burnout'],
  },
  {
    heading: 'Couples & Family',
    items: ['Couples Counseling', 'Marriage Counseling', 'Communication', 'Premarital Counseling', 'Conflict Resolution', 'Family Therapy'],
  },
  {
    heading: 'Child & Teen (Ages 7+)',
    items: ['Anxiety', 'Behavioral Concerns', 'School Issues', 'Emotional Regulation', 'Self-Esteem'],
  },
  {
    heading: 'Grief, Women’s & More',
    items: ['Grief & Loss Counseling', 'Women’s Mental Health', 'Narcissistic Abuse Recovery', 'Life Transitions'],
  },
];

const MODALITIES = [
  'EMDR Therapy',
  'Accelerated Resolution Therapy (ART)',
  'Internal Family Systems (IFS)',
  'Cognitive Behavioral Therapy (CBT)',
  'Acceptance and Commitment Therapy (ACT)',
  'Dialectical Behavior Therapy (DBT)',
  'Mindfulness-Based Therapy',
  'Somatic Therapy',
  'Walk-and-Talk Therapy',
  'Telehealth Counseling',
  'Group Therapy',
];

export default function Services() {
  return (
    <>
      <Seo
        title="Counseling Services in Chesapeake, VA"
        description="Trauma, EMDR, ART, anxiety, couples, family, child & teen (ages 7+), and women’s counseling in Chesapeake, VA. Personalized, evidence-based care."
        path="/services"
      />
      <PageHero
        eyebrow="Services"
        title="How We"
        highlight="Can Help"
        subtitle="Personalized, evidence-based care for children (ages 7+), teens, adults, couples, and families — in Chesapeake, VA and via telehealth throughout Virginia."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Services' }]}
      />

      <ServicesGrid mode="services" />

      {/* Trauma Intensives promo band */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Link
            to="/trauma-intensives"
            className="card-soft p-8 md:p-10 grid md:grid-cols-[80px_1fr_auto] gap-6 items-center reveal group hover:shadow-lg transition"
            style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.10), rgba(184,190,164,0.14))' }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-sm">
              <Zap className="w-7 h-7 text-[color:var(--dusty-rose)]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="eyebrow mb-2">New — Accelerated Healing</div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[color:var(--deep-sage)] font-bold mb-2">
                EMDR &amp; ART Trauma Intensives
              </h3>
              <p className="text-[color:var(--taupe-gray)] leading-relaxed">
                Ready to make meaningful progress in a focused, personalized format? Explore our half-day and full-day trauma intensive packages.
              </p>
            </div>
            <div className="btn-primary md:justify-self-end whitespace-nowrap">
              Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* Comprehensive "What We Treat" */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Comprehensive Care</div>
            <h2 className="section-title mb-3">
              What We <span className="text-[color:var(--dusty-rose)]">Treat</span>
            </h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed">
              We provide care for <strong className="text-[color:var(--deep-sage)]">children ages 7+, teens, adults, couples, and families</strong> across a wide range of concerns.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {AREAS.map((a) => (
              <div key={a.heading} className="card-soft p-6 reveal">
                <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] font-bold mb-3">{a.heading}</h3>
                <ul className="space-y-2">
                  {a.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[color:var(--taupe-gray)]">
                      <Check className="w-3.5 h-3.5 text-[color:var(--sage-dark)] mt-1 flex-shrink-0" strokeWidth={2.5} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modalities */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Evidence-Based Approaches</div>
            <h2 className="section-title mb-3">
              Treatment <span className="text-[color:var(--dusty-rose)]">Modalities</span>
            </h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed">
              Our clinicians are trained across a wide range of evidence-based therapeutic approaches, integrated to fit your unique needs.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 reveal">
            {MODALITIES.map((m) => (
              <span
                key={m}
                className="px-4 py-2 rounded-full text-sm font-medium text-[color:var(--sage-dark)] border"
                style={{ background: 'rgba(184,190,164,0.15)', borderColor: 'var(--soft-border)' }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Group Therapy + Supervision + Careers */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6">
          <div className="card-soft p-7 reveal">
            <div className="w-12 h-12 rounded-2xl serviceCard-icon flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] mb-2">Group Therapy</h3>
            <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed mb-3">
              Small, supportive groups are launching soon. Flyers and enrollment details will be posted here as we finalize our current offerings.
            </p>
            <Link to="/contact" className="text-sm text-[color:var(--dusty-rose)] font-semibold hover:underline inline-flex items-center gap-1">
              Ask to be notified <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="card-soft p-7 reveal">
            <div className="w-12 h-12 rounded-2xl serviceCard-icon flex items-center justify-center mb-4">
              <GraduationCap className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] mb-2">Clinical Supervision</h3>
            <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed mb-3">
              Our founder, Jodi S. Baker-Hutchinson, LCSW, is a Board-approved Clinical Supervisor with the Virginia Board of Social Work &amp; NASW — offering supervision for aspiring LCSWs across Virginia.
            </p>
            <a href="mailto:admin@chesapeakecounselingcenter.com" className="text-sm text-[color:var(--dusty-rose)] font-semibold hover:underline inline-flex items-center gap-1">
              Inquire about supervision <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="card-soft p-7 reveal">
            <div className="w-12 h-12 rounded-2xl serviceCard-icon flex items-center justify-center mb-4">
              <BriefcaseBusiness className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] mb-2">We’re Hiring</h3>
            <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed mb-3">
              We are always looking for talented Residents in Counseling and licensed clinicians who share our compassionate, client-centered mission. Please email your resume to join our team.
            </p>
            <a href="mailto:admin@chesapeakecounselingcenter.com?subject=Career%20Inquiry" className="text-sm text-[color:var(--dusty-rose)] font-semibold hover:underline inline-flex items-center gap-1">
              Email your resume <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
